import React, { useEffect, useRef, useState } from "react";
import { useAISupport } from "../contexts/AISupportContext";
import { AISupportLang, dirFor, t } from "../i18n/aiSupport";
import { AssistantText } from "./AssistantText";
import { getTextDirection } from "../utils/textDirection";
import { formatClinicRelative } from "../utils/israelTime";
import { AISupportOutcome, AISupportPendingAction } from "../types/api";
import AISupportWalkthrough from "./AISupportWalkthrough";

const POSITION_STORAGE_KEY = "aiSupportWidgetPosition";
const NOTICE_STORAGE_KEY = "aiSupportNoticeSeen";
const WIDGET_WIDTH = 380;
const WIDGET_HEIGHT = 560;

/** Above modals (1000-1300) and the call widget (2000). */
const Z_INDEX = 2100;

const TEAL = "#3A7380";
const TEAL_SOFT = "#B4D9D4";

/**
 * What the therapist is being asked to approve, in their words.
 *
 * The tool name is an internal one and the arguments are ids, so neither is
 * shown: the assistant has just said in ordinary language what it is about to
 * do and to whom, and this line only has to name the action the buttons apply
 * to. An action with no phrasing here falls back to nothing rather than to its
 * internal name.
 */
const ACTION_LABELS: Record<string, { he: string; en: string }> = {
  reset_2fa_setup: {
    he: "איפוס האימות הדו-שלבי",
    en: "Reset two-factor authentication",
  },
};

const actionLabel = (lang: AISupportLang, action: AISupportPendingAction): string => {
  const entry = ACTION_LABELS[action.action_name];
  return entry ? entry[lang] ?? entry.he : "";
};

/**
 * The small header controls. Labelled rather than iconographic: a bare glyph
 * gave no clue what it did, and starting a new conversation discards the
 * current one, so it should say so.
 */
const headerButtonStyle: React.CSSProperties = {
  border: "1px solid #8fb3ae",
  background: "transparent",
  borderRadius: 4,
  padding: "2px 8px",
  fontSize: 11,
  lineHeight: 1.7,
  whiteSpace: "nowrap",
  cursor: "pointer",
  color: "#34505a",
  position: "relative",
  zIndex: 1,
};

/**
 * Floating AI Support assistant. Mounted once in App.tsx, like CallWidget, so
 * the conversation survives navigation between screens.
 */
const AISupportWidget: React.FC = () => {
  const {
    isOpen, lang, turns, isBusy, activity, pendingAction, isEscalated,
    errorMessage, open, close, setLang, send,
    submitVerdict, sendFeedbackNote, reset, walkthrough, dismissWalkthrough,
    history, historyFilter, setHistoryFilter, isHistoryOpen, openHistory,
    closeHistory, resumeSession, unreadCount, sessionId, lastWalkthrough,
    reopenWalkthrough, decidePendingAction, isDeciding, isClosable,
    isCloseAskOpen, openCloseAsk, dismissCloseAsk,
  } = useAISupport();

  const [draft, setDraft] = useState("");
  const [showNotice, setShowNotice] = useState(false);
  const [solvedNotice, setSolvedNotice] = useState(false);
  // Pressing × on a conversation that still needs a verdict asks first. The
  // × is what a therapist reaches for when they are done, and it used to
  // minimise silently -- leaving the conversation open for days and the
  // feedback, the only measure of whether the answer was any good, ungiven.
  //
  // Asked EVERY time it is dismissed, not once per conversation: a therapist
  // who waves it away has not answered the question, and the conversation is
  // still open the next time they leave.
  const [closeNote, setCloseNote] = useState("");
  // Written feedback, offered after the verdict rather than instead of it: the
  // button is one click and always lands, while the sentence is optional and is
  // usually thought of a moment later. null = not being asked for.
  const [feedbackDraft, setFeedbackDraft] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(() => {
    try {
      const saved = localStorage.getItem(POSITION_STORAGE_KEY);
      if (!saved) return null;
      const pos = JSON.parse(saved);
      if (typeof pos?.x !== "number" || typeof pos?.y !== "number") return null;
      // Clamp in case the viewport shrank since the position was stored.
      return {
        x: Math.min(Math.max(0, pos.x), window.innerWidth - WIDGET_WIDTH),
        y: Math.min(Math.max(0, pos.y), window.innerHeight - 120),
      };
    } catch {
      return null;
    }
  });

  const widgetRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  const dir = dirFor(lang);
  // Unfinished conversations first-class: a therapist returning to the assistant
  // is nearly always coming back to one of those, not browsing the archive.
  const visibleHistory =
    historyFilter === "open"
      ? history.filter((session) => session.status !== "closed")
      : history;

  useEffect(() => {
    if (!isOpen) return;
    try {
      setShowNotice(!localStorage.getItem(NOTICE_STORAGE_KEY));
    } catch {
      setShowNotice(false);
    }
  }, [isOpen]);

  useEffect(() => { setSolvedNotice(false); }, [sessionId]);

  // Keep the newest turn in view as the answer streams in.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, isBusy, pendingAction]);

  const dismissNotice = () => {
    setShowNotice(false);
    try {
      localStorage.setItem(NOTICE_STORAGE_KEY, "1");
    } catch {
      /* the notice will simply show again next time */
    }
  };

  const onHeaderPointerDown = (e: React.PointerEvent) => {
    // The header doubles as the drag handle and as the toolbar. Capturing the
    // pointer here would swallow the click on the buttons inside it, so bail
    // out when the gesture starts on a control and let it behave normally.
    if ((e.target as HTMLElement).closest("button")) return;
    const rect = widgetRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };

  const onHeaderPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    const el = widgetRef.current;
    if (!drag || !el) return;
    setPosition({
      x: Math.min(Math.max(0, drag.origX + (e.clientX - drag.startX)), window.innerWidth - el.offsetWidth),
      y: Math.min(Math.max(0, drag.origY + (e.clientY - drag.startY)), window.innerHeight - el.offsetHeight),
    });
  };

  const onHeaderPointerUp = () => {
    if (dragRef.current && position) {
      try {
        localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
      } catch {
        /* position just won't persist */
      }
    }
    dragRef.current = null;
  };

  const submitDraft = () => {
    const text = draft.trim();
    if (!text || isBusy) return;
    setDraft("");
    void send(text);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter newlines — same as the WhatsApp composer.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitDraft();
    }
  };

  const overlay = walkthrough ? (
    <AISupportWalkthrough
      walkthrough={walkthrough}
      lang={lang}
      onClose={dismissWalkthrough}
    />
  ) : null;

  if (!isOpen) {
    return (
      <>
        {overlay}
        <button
        type="button"
        onClick={() => void open()}
        aria-label={t(lang, "launcherLabel")}
        title={t(lang, "title")}
        style={{
          position: "fixed", bottom: 24, insetInlineStart: 24, zIndex: Z_INDEX,
          width: 52, height: 52, borderRadius: "50%", border: "none",
          background: TEAL, color: "#fff", fontSize: 22, cursor: "pointer",
          boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
        }}
        >
          ✦
          {unreadCount > 0 && (
            <span
              aria-label={t(lang, "newAnswer")}
              style={{
                position: "absolute", top: -2, insetInlineEnd: -2,
                minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9,
                background: "#8E2F41", color: "#fff", fontSize: 11, lineHeight: "18px",
                fontWeight: 600, border: "2px solid #fff",
              }}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </>
    );
  }

  const positioned = position
    ? { left: position.x, top: position.y }
    : { bottom: 24, insetInlineStart: 24 };

  return (
    <>
      {overlay}
    <div
      ref={widgetRef}
      dir={dir}
      style={{
        position: "fixed", zIndex: Z_INDEX, width: WIDGET_WIDTH,
        maxWidth: "calc(100vw - 24px)", height: WIDGET_HEIGHT,
        maxHeight: "calc(100vh - 48px)", display: "flex", flexDirection: "column",
        background: "#fff", borderRadius: 12, overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.28)", border: "1px solid #d9e4e2",
        ...positioned,
      }}
    >
      <div
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        style={{
          display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
          background: TEAL_SOFT, borderBottom: "1px solid #cbdedb", cursor: "move",
          touchAction: "none",
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: TEAL }} />
        <span
          style={{
            flex: 1, fontWeight: 600, fontSize: 14,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {t(lang, "title")}
        </span>
        <button
          data-ai-id="assistant-language-toggle"
          type="button"
          onClick={() => setLang(lang === "he" ? "en" : ("he" as AISupportLang))}
          style={headerButtonStyle}
        >
          {t(lang, "langToggle")}
        </button>
        <button
          data-ai-id="assistant-history-toggle"
          type="button"
          onClick={isHistoryOpen ? closeHistory : openHistory}
          title={t(lang, "history")}
          style={headerButtonStyle}
        >
          {isHistoryOpen ? t(lang, "back") : t(lang, "history")}
        </button>
        <button
          data-ai-id="assistant-new-conversation"
          type="button"
          onClick={reset}
          title={t(lang, "newConversation")}
          style={headerButtonStyle}
        >
          {t(lang, "newConversation")}
        </button>
        <button
          type="button"
          onClick={() => {
            // Never trap them: ask once, and if they are already being asked
            // (or there is nothing to close) the × just minimises.
            //
            // !isHistoryOpen matters as much as the rest: the ask renders in
            // the footer, which the history list replaces. Without it the ×
            // would set the flag with nothing on screen to answer, and the
            // button would appear to do nothing at all.
            if (isClosable && !isHistoryOpen && !isCloseAskOpen && !solvedNotice) {
              openCloseAsk();
            } else {
              close();
            }
          }}
          aria-label={t(lang, "close")}
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 18, lineHeight: 1, position: "relative", zIndex: 1 }}
        >
          ×
        </button>
      </div>

      {showNotice && (
        <div style={{ padding: "10px 14px", background: "#f4f8f7", borderBottom: "1px solid #e3ecea", fontSize: 12, color: "#4a5a5c" }}>
          {t(lang, "firstUseNotice")}
          <button
            data-ai-id="assistant-dismiss-notice"
            type="button"
            onClick={dismissNotice}
            style={{ marginInlineStart: 8, border: "none", background: "transparent", color: TEAL, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
          >
            {t(lang, "gotIt")}
          </button>
        </div>
      )}

      {isHistoryOpen ? (
        <div style={{ flex: 1, overflowY: "auto", background: "#f7faf9" }}>
          <div style={{ display: "flex", gap: 6, padding: "10px 14px 6px" }}>
            {([
              ["open", "filterOpen"],
              ["all", "filterAll"],
            ] as Array<["open" | "all", "filterOpen" | "filterAll"]>).map(([value, key]) => {
              const active = historyFilter === value;
              return (
                <button
                  data-ai-id="assistant-history-filter"
                  key={value}
                  type="button"
                  onClick={() => setHistoryFilter(value)}
                  style={{
                    border: `1px solid ${active ? TEAL : "#dfe7e6"}`,
                    background: active ? "#DCECEA" : "#fff",
                    color: active ? TEAL : "#6b7b7a",
                    borderRadius: 12, padding: "2px 12px", fontSize: 11.5,
                    cursor: "pointer", fontWeight: active ? 600 : 400,
                  }}
                >
                  {t(lang, key)}
                </button>
              );
            })}
          </div>
          {visibleHistory.length === 0 && (
            <p style={{ color: "#7a8a89", fontSize: 13, margin: 0, padding: 14 }}>
              {/* The filter decides what "empty" means: under "open" there may
                  be plenty of past conversations, just none still going, and
                  saying there are none at all reads as lost history. */}
              {t(lang, historyFilter === "open" ? "historyEmptyOpen" : "historyEmpty")}
            </p>
          )}
          {visibleHistory.map((session) => (
            <button
              data-ai-id="assistant-history-item"
              key={session.id}
              type="button"
              onClick={() => void resumeSession(session.id)}
              style={{
                display: "block", width: "100%", textAlign: dir === "rtl" ? "right" : "left",
                border: "none", borderBottom: "1px solid #e6eeed", background: "#fff",
                padding: "10px 14px", cursor: "pointer", fontFamily: "inherit",
              }}
            >
              <div dir="auto" style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {session.title || t(lang, "title")}
              </div>
              <div style={{ fontSize: 11, color: "#8b9a99", marginTop: 3 }}>
                {formatClinicRelative(session.last_activity_at || session.updated_at, lang)}
                {session.outcome === "fixed"
                  ? ` · ${t(lang, "verdictFixed")}`
                  : session.outcome === "didnt_help"
                  ? ` · ${t(lang, "verdictDidntHelp")}`
                  : ""}
                {session.status === "escalated" ? ` · ${t(lang, "withSupport")}` : ""}
              </div>
            </button>
          ))}
        </div>
      ) : (
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 14, background: "#f7faf9" }}>
        {turns.length === 0 && (
          <p style={{ color: "#7a8a89", fontSize: 13, margin: 0 }}>{t(lang, "emptyState")}</p>
        )}

        {turns.map((turn) => (
          <div
            key={turn.id}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: turn.role === "user" ? "flex-start" : "flex-end",
              marginBottom: 10,
            }}
          >
            {turn.role === "support" && (
              <span style={{ fontSize: 10.5, color: "#8A5A12", marginBottom: 3, fontWeight: 600 }}>
                {t(lang, "fromSupport")}{turn.author ? ` · ${turn.author}` : ""}
              </span>
            )}
            <div
              // dir="auto" per message: a Hebrew answer containing an English
              // code snippet has to read correctly either way.
              dir="auto"
              style={{
                maxWidth: "86%", padding: "8px 12px", borderRadius: 12,
                fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-wrap",
                background: turn.role === "user" ? TEAL : "#fff",
                color: turn.role === "user" ? "#fff" : "#16242a",
                border:
                  turn.role === "user" ? "none"
                    : turn.role === "support" ? "1px solid #C9A227"
                    : "1px solid #e0eae8",
                textAlign: getTextDirection(turn.text) === "rtl" ? "right" : "left",
              }}
            >
              <AssistantText text={turn.text} />
            </div>
          </div>
        ))}

        {!walkthrough && lastWalkthrough && (
          <button
            type="button"
            onClick={reopenWalkthrough}
            style={{
              alignSelf: "center", margin: "2px 0 8px", padding: "5px 14px",
              borderRadius: 14, border: `1px solid ${TEAL}`, background: "#fff",
              color: TEAL, fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            ↻ {t(lang, "resumeGuide")}
            {lastWalkthrough.title ? ` · ${lastWalkthrough.title}` : ""}
          </button>
        )}

        {isBusy && (
          <p style={{ color: "#7a8a89", fontSize: 12, margin: "4px 0" }}>
            {activity ? t(lang, "working") : t(lang, "thinking")}
          </p>
        )}

        {pendingAction && (
          <div style={{ border: `1px solid ${TEAL}`, background: "#eef6f5", borderRadius: 8, padding: "10px 12px", fontSize: 12.5 }}>
            {/* An action this person administers anyway is decided here rather
                than queued: for them the wait was the whole cost. Everyone else
                sees the same card saying support has it. */}
            {pendingAction.may_approve ? (
              <>
                <div>{t(lang, "approvalYours")}</div>
                <div style={{ margin: "6px 0", fontWeight: 600 }}>
                  {actionLabel(lang, pendingAction)}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => decidePendingAction(true)}
                    disabled={isDeciding}
                    style={{
                      padding: "5px 14px", borderRadius: 14, border: "none",
                      background: TEAL, color: "#fff", fontSize: 12, fontWeight: 600,
                      cursor: isDeciding ? "default" : "pointer",
                      opacity: isDeciding ? 0.6 : 1,
                    }}
                  >
                    {t(lang, "approveAction")}
                  </button>
                  <button
                    onClick={() => decidePendingAction(false)}
                    disabled={isDeciding}
                    style={{
                      padding: "5px 14px", borderRadius: 14,
                      border: "1px solid #c4d4d2", background: "#fff",
                      color: "#4a5a59", fontSize: 12, fontWeight: 600,
                      cursor: isDeciding ? "default" : "pointer",
                      opacity: isDeciding ? 0.6 : 1,
                    }}
                  >
                    {t(lang, "denyAction")}
                  </button>
                </div>
              </>
            ) : (
              t(lang, "awaitingApproval")
            )}
          </div>
        )}

        {isEscalated && (
          <div style={{ border: "1px solid #c9a227", background: "#fdf6e3", borderRadius: 8, padding: "10px 12px", fontSize: 12.5 }}>
            {t(lang, "escalated")}
          </div>
        )}

        {errorMessage && (
          <div style={{ border: "1px solid #d4a2a9", background: "#fdf0f1", borderRadius: 8, padding: "10px 12px", fontSize: 12.5 }}>
            {errorMessage || t(lang, "error")}
          </div>
        )}
      </div>
      )}

      {/* The composer keeps its LTR order in Hebrew on purpose. The widget as a
          whole flips with the language, and flipping this row too put the send
          button on the left, where nobody looks for it: the arrow is the same
          control in both languages and people reach for it in the same corner.
          The textarea still reads its own direction from what is typed. */}
      {!isHistoryOpen && (
      <div style={{ borderTop: "1px solid #e3ecea", padding: 10, background: "#fff", display: "flex", gap: 8, alignItems: "flex-end", direction: "ltr" }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t(lang, "placeholder")}
          rows={1}
          dir="auto"
          style={{
            flex: 1, resize: "none", border: "1px solid #dfe9e7", borderRadius: 16,
            padding: "7px 12px", fontSize: 13.5, maxHeight: 96, outline: "none",
            fontFamily: "inherit",
          }}
        />
        <button
          type="button"
          onClick={submitDraft}
          disabled={!draft.trim() || isBusy}
          aria-label={t(lang, "send")}
          style={{
            width: 32, height: 32, borderRadius: "50%", border: "none", flex: "none",
            background: draft.trim() && !isBusy ? TEAL : "#c4d4d2",
            color: "#fff", cursor: draft.trim() && !isBusy ? "pointer" : "default",
          }}
        >
          ➤
        </button>
      </div>
      )}

      {!isHistoryOpen && sessionId && (
        <div
          style={{
            display: "flex", gap: 6, borderTop: "1px solid #e3ecea",
            background: "#fff", padding: "8px 10px", flexWrap: "wrap",
          }}
        >
          {isCloseAskOpen ? (
            // Outcome and comment in one step, because they were on their way
            // out: sending them through the normal two-stage flow (verdict,
            // then "anything to add?") asks somebody who has already left.
            <div style={{ width: "100%" }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "#33403f" }}>
                {t(lang, "closeOnDismissTitle")}
              </div>
              <div style={{ fontSize: 12, color: "#5a6b6a", marginTop: 3 }}>
                {t(lang, "closeOnDismissBody")}
              </div>
              <textarea
                value={closeNote}
                onChange={(e) => setCloseNote(e.target.value)}
                placeholder={t(lang, "feedbackPlaceholder")}
                rows={2}
                dir="auto"
                style={{
                  width: "100%", marginTop: 6, resize: "none", fontSize: 12,
                  padding: "5px 8px", borderRadius: 6, fontFamily: "inherit",
                  border: "1px solid #dfe9e7", outline: "none",
                }}
              />
              <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                {([
                  ["fixed", "verdictFixed", TEAL],
                  ["helped", "verdictHelped", "#4b7f78"],
                  ["didnt_help", "verdictDidntHelp", "#8a9695"],
                  ["need_human", "verdictNeedHuman", "#a2761f"],
                ] as Array<[AISupportOutcome, "verdictFixed" | "verdictHelped" | "verdictDidntHelp" | "verdictNeedHuman", string]>).map(
                  ([outcome, key, color]) => (
                    <button
                      data-ai-id="assistant-close-verdict-button"
                      key={outcome}
                      type="button"
                      onClick={() => {
                        void submitVerdict(outcome, closeNote);
                        setCloseNote("");
                        // Handing it to a person is not leaving: they will want
                        // to see that it was passed on, so the panel stays.
                        if (outcome === "need_human") return;
                        setSolvedNotice(true);
                        close();
                      }}
                      style={{
                        flex: "1 1 auto", padding: "5px 8px", borderRadius: 5, fontSize: 12,
                        fontWeight: 500, cursor: "pointer", background: "#f7faf9",
                        border: `1px solid ${color}`, color, whiteSpace: "nowrap",
                      }}
                    >
                      {t(lang, key)}
                    </button>
                  )
                )}
              </div>
              <button
                type="button"
                onClick={() => { dismissCloseAsk(); setCloseNote(""); close(); }}
                style={{
                  marginTop: 6, background: "none", border: "none", padding: 0,
                  fontSize: 11.5, color: "#7a8a89", cursor: "pointer",
                  textDecoration: "underline", textUnderlineOffset: 3,
                }}
              >
                {t(lang, "closeOnDismissSkip")}
              </button>
            </div>
          ) : solvedNotice ? (
            <div style={{ width: "100%" }}>
              <span style={{ fontSize: 12, color: TEAL, fontWeight: 600, padding: "4px 2px" }}>
                ✓ {feedbackSent ? t(lang, "feedbackThanks") : t(lang, "verdictThanks")}
              </span>
              {!feedbackSent && feedbackDraft === null && (
                <button
                  data-ai-id="assistant-feedback-open"
                  type="button"
                  onClick={() => setFeedbackDraft("")}
                  style={{
                    display: "block", marginTop: 5, background: "none", border: "none",
                    padding: 0, fontSize: 12, color: "#4b7f78", cursor: "pointer",
                    textDecoration: "underline", textUnderlineOffset: 3,
                  }}
                >
                  {t(lang, "feedbackPrompt")}
                </button>
              )}
              {!feedbackSent && feedbackDraft !== null && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: "#7a8a89", marginBottom: 4 }}>
                    {t(lang, "feedbackHint")}
                  </div>
                  <textarea
                    data-ai-id="assistant-feedback-text"
                    autoFocus
                    dir="auto"
                    rows={3}
                    value={feedbackDraft}
                    onChange={(e) => setFeedbackDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setFeedbackDraft(null);
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        void (async () => {
                          if (await sendFeedbackNote(feedbackDraft)) setFeedbackSent(true);
                          setFeedbackDraft(null);
                        })();
                      }
                    }}
                    placeholder={t(lang, "feedbackPlaceholder")}
                    style={{
                      width: "100%", boxSizing: "border-box", resize: "vertical",
                      border: "1px solid #cdd8d7", borderRadius: 6, padding: "6px 8px",
                      fontSize: 12.5, fontFamily: "inherit", color: "#33403f",
                    }}
                  />
                  <button
                    data-ai-id="assistant-feedback-send"
                    type="button"
                    disabled={!feedbackDraft.trim()}
                    onClick={() => {
                      void (async () => {
                        if (await sendFeedbackNote(feedbackDraft)) setFeedbackSent(true);
                        setFeedbackDraft(null);
                      })();
                    }}
                    style={{
                      marginTop: 5, padding: "4px 12px", borderRadius: 5, fontSize: 12,
                      cursor: feedbackDraft.trim() ? "pointer" : "default",
                      border: `1px solid ${feedbackDraft.trim() ? TEAL : "#dfe7e6"}`,
                      background: feedbackDraft.trim() ? "#DCECEA" : "#fff",
                      color: feedbackDraft.trim() ? TEAL : "#9aa8a7",
                    }}
                  >
                    {t(lang, "feedbackSend")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            // One permanent row rather than a transient prompt above the
            // composer plus a second row below it: the same choices were being
            // offered twice, in two places, worded differently.
            //
            // "Helped" sits between solved and didn't-help on purpose. Without
            // it a conversation that was genuinely useful but resolved nothing
            // had to be filed as a resolution it was not, or a failure it was
            // not, or left open indefinitely — and the last is what therapists
            // actually did.
            ([
              ["fixed", "verdictFixed", TEAL],
              ["helped", "verdictHelped", "#4b7f78"],
              ["didnt_help", "verdictDidntHelp", "#8a9695"],
              ["need_human", "verdictNeedHuman", "#a2761f"],
            ] as Array<[AISupportOutcome, "verdictFixed" | "verdictHelped" | "verdictDidntHelp" | "verdictNeedHuman", string]>).map(
              ([outcome, key, color]) => (
                <button
                  data-ai-id="assistant-verdict-button"
                  key={outcome}
                  type="button"
                  onClick={() => {
                    void submitVerdict(outcome);
                    if (outcome !== "need_human") setSolvedNotice(true);
                  }}
                  style={{
                    flex: "1 1 auto", padding: "5px 8px", borderRadius: 5, fontSize: 12,
                    fontWeight: 500, cursor: "pointer", background: "#f7faf9",
                    border: `1px solid ${color}`, color, whiteSpace: "nowrap",
                  }}
                >
                  {t(lang, key)}
                </button>
              )
            )
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default AISupportWidget;
